// src/pages/Users.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users as UsersIcon,
  Plus,
  Search,
  Edit2,
  Trash2,
  KeyRound,
  ShieldCheck,
  Building,
  Power,
  ShieldAlert,
} from 'lucide-react';
import { User, Department, Role, PaginatedResult } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { formatDateTime, formatDateOnly } from '../lib/date';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Pagination } from '../components/ui/Pagination';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { EmptyState } from '../components/ui/EmptyState';

export const Users: React.FC = () => {
  const { user: currentUser, can } = useAuth();
  const { settings } = useSettings();
  const { success, error: toastError } = useToast();

  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });

  // Add / Edit User Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    department_id: '',
    role_id: '',
    employee_id: '',
    job_title: '',
    phone: '',
    status: 'active',
  });
  const [formLoading, setFormLoading] = useState(false);

  // Reset Password Modal
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [newTempPassword, setNewTempPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Delete User Confirmation
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; user: User | null; loading: boolean }>({
    open: false,
    user: null,
    loading: false,
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        pageSize: settings.default_page_size || '25',
      });
      if (search) params.append('search', search);
      if (departmentFilter) params.append('department', departmentFilter);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);

      const res = await apiRequest<PaginatedResult<User>>(`/api/users?${params.toString()}`);
      setUsers(res.items || []);
      setPagination(res.pagination);
    } catch (err: any) {
      toastError(err.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [page, search, departmentFilter, roleFilter, statusFilter, settings.default_page_size, toastError]);

  // Load auxiliary data (departments & roles)
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        if (can('departments.view')) {
          const depts = await apiRequest<Department[]>('/api/departments');
          setDepartments(depts || []);
        }
        if (can('roles.view')) {
          const r = await apiRequest<Role[]>('/api/roles');
          setRoles(r || []);
        }
      } catch {
        // non-blocking
      }
    };
    loadMetadata();
  }, [can]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers();
    }, 200);
    return () => clearTimeout(timer);
  }, [loadUsers]);

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({
      full_name: '',
      email: '',
      password: '',
      confirm_password: '',
      department_id: currentUser?.department ? String(currentUser.department.id) : '',
      role_id: roles.length > 0 ? String(roles[roles.length - 1].id) : '',
      employee_id: '',
      job_title: '',
      phone: '',
      status: 'active',
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name,
      email: user.email,
      password: '',
      confirm_password: '',
      department_id: user.department_id ? String(user.department_id) : '',
      role_id: String(user.role_id),
      employee_id: user.employee_id || '',
      job_title: user.job_title || '',
      phone: user.phone || '',
      status: user.status,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.full_name || !formData.email || !formData.role_id) {
      toastError('Full Name, Email, and Role are mandatory.');
      return;
    }

    if (!editingUser) {
      if (!formData.password || formData.password.length < 8) {
        toastError('Temporary password must be at least 8 characters long.');
        return;
      }
      if (formData.password !== formData.confirm_password) {
        toastError('Passwords do not match.');
        return;
      }
    }

    setFormLoading(true);
    try {
      const payload: any = {
        full_name: formData.full_name,
        email: formData.email,
        department_id: formData.department_id ? Number(formData.department_id) : null,
        role_id: Number(formData.role_id),
        employee_id: formData.employee_id,
        job_title: formData.job_title,
        phone: formData.phone,
        status: formData.status,
      };

      if (!editingUser) {
        payload.password = formData.password;
        payload.force_password_change = true;
        await apiRequest('/api/users', { method: 'POST', body: JSON.stringify(payload) });
        success(`User account for ${formData.full_name} created successfully.`);
      } else {
        await apiRequest(`/api/users/${editingUser.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        success(`User ${formData.full_name} updated successfully.`);
      }

      setModalOpen(false);
      loadUsers();
    } catch (err: any) {
      toastError(err.message || 'Failed to save user.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetUser || !newTempPassword || newTempPassword.length < 8) {
      toastError('Temporary password must be at least 8 characters long.');
      return;
    }

    setResetLoading(true);
    try {
      await apiRequest(`/api/users/${resetUser.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword: newTempPassword }),
      });
      success(`Password reset for ${resetUser.full_name}. They must change it upon next login.`);
      setResetModalOpen(false);
      setResetUser(null);
      setNewTempPassword('');
      loadUsers();
    } catch (err: any) {
      toastError(err.message || 'Failed to reset password.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!confirmDelete.user) return;
    setConfirmDelete(prev => ({ ...prev, loading: true }));
    try {
      await apiRequest(`/api/users/${confirmDelete.user.id}`, { method: 'DELETE' });
      success(`User account deactivated and deleted.`);
      setConfirmDelete({ open: false, user: null, loading: false });
      loadUsers();
    } catch (err: any) {
      toastError(err.message || 'Failed to delete user.');
      setConfirmDelete(prev => ({ ...prev, loading: false }));
    }
  };

  const handleToggleStatus = async (user: User) => {
    const nextStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await apiRequest(`/api/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: nextStatus }),
      });
      success(`User status updated to ${nextStatus}.`);
      loadUsers();
    } catch (err: any) {
      toastError(err.message || 'Failed to change status.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Title & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">User Management</h2>
          <p className="text-xs text-slate-500">
            Control employee accounts, assign departments, and define role-based access
          </p>
        </div>
        {can('users.create') && (
          <Button onClick={handleOpenCreate} icon={<Plus className="w-4 h-4" />}>
            Add User
          </Button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Input
            placeholder="Search name, email, employee ID..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            icon={<Search className="w-4 h-4" />}
          />
        </div>
        <div>
          <Select
            value={departmentFilter}
            onChange={e => {
              setDepartmentFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All Departments' },
              ...departments.map(d => ({ value: d.id, label: d.name })),
            ]}
          />
        </div>
        <div>
          <Select
            value={roleFilter}
            onChange={e => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All Roles' },
              ...roles.map(r => ({ value: r.id, label: r.name })),
            ]}
          />
        </div>
        <div>
          <Select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'suspended', label: 'Suspended' },
            ]}
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <TableSkeleton rows={8} columns={7} />
        ) : users.length === 0 ? (
          <EmptyState
            icon={<UsersIcon className="w-10 h-10 text-slate-400" />}
            title="No users found"
            description="Adjust your search criteria or add new personnel to the directory."
            actionLabel={can('users.create') ? 'Add User' : undefined}
            onAction={handleOpenCreate}
          />
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3.5">Name / Email</th>
                    <th className="px-6 py-3.5">Department</th>
                    <th className="px-6 py-3.5">Role</th>
                    <th className="px-6 py-3.5">Status</th>
                    <th className="px-6 py-3.5">Last Login</th>
                    <th className="px-6 py-3.5">Created</th>
                    <th className="px-6 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map(u => {
                    const isSelf = currentUser?.id === u.id;
                    const isSuperAdmin = Boolean(u.is_system_role);

                    return (
                      <tr key={u.id} className="hover:bg-slate-50/70 transition">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-700 font-bold text-xs uppercase border border-slate-200">
                              {u.full_name.substring(0, 2)}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                                <span>{u.full_name}</span>
                                {isSelf && (
                                  <span className="text-[10px] bg-indigo-50 text-indigo-700 font-bold px-1.5 py-0.5 rounded border border-indigo-200">
                                    You
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500">{u.email}</div>
                              {u.job_title && (
                                <div className="text-[11px] text-slate-400">{u.job_title}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs font-medium text-slate-800">
                          {u.department_name ? (
                            <span className="inline-flex items-center gap-1">
                              <Building className="w-3.5 h-3.5 text-slate-400" />
                              {u.department_name}
                            </span>
                          ) : (
                            <span className="text-slate-400 italic">None</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 border border-slate-200">
                            <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" />
                            {u.role_name}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <Badge status={u.status} />
                          {Boolean(u.force_password_change) && (
                            <span className="block text-[10px] text-amber-600 font-medium mt-0.5">
                              Temp Password
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500 whitespace-nowrap">
                          {formatDateTime(u.last_login_at, settings.timezone)}
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500 whitespace-nowrap">
                          {formatDateOnly(u.created_at, settings.timezone)}
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            {can('users.edit') && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleToggleStatus(u)}
                                  disabled={isSelf}
                                  title={u.status === 'active' ? 'Deactivate user' : 'Activate user'}
                                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition disabled:opacity-30"
                                >
                                  <Power className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setResetUser(u);
                                    setNewTempPassword('Temp@' + Math.floor(100000 + Math.random() * 900000));
                                    setResetModalOpen(true);
                                  }}
                                  title="Reset password"
                                  className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition"
                                >
                                  <KeyRound className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleOpenEdit(u)}
                                  disabled={isSelf}
                                  title={isSelf ? 'Cannot edit self role' : 'Edit user'}
                                  className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition disabled:opacity-30"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {can('users.delete') && (
                              <button
                                type="button"
                                onClick={() => setConfirmDelete({ open: true, user: u, loading: false })}
                                disabled={isSelf}
                                title={isSelf ? 'Cannot delete self' : 'Delete user'}
                                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded transition disabled:opacity-30"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              total={pagination.total}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Add / Edit User Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingUser ? `Edit User: ${editingUser.full_name}` : 'Add New User'}
        description="Configure identity, department assignment, and RBAC authorization role."
        maxWidth="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              required
              value={formData.full_name}
              onChange={e => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
              placeholder="e.g. John Doe"
            />
            <Input
              label="Work Email"
              type="email"
              required
              value={formData.email}
              onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="e.g. jdoe@company.com"
            />
          </div>

          {!editingUser && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
              <Input
                label="Temporary Password"
                type="password"
                required
                minLength={8}
                value={formData.password}
                onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Min 8 characters"
                helperText="User must change password on first login"
              />
              <Input
                label="Confirm Password"
                type="password"
                required
                minLength={8}
                value={formData.confirm_password}
                onChange={e => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Re-enter password"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Department"
              value={formData.department_id}
              onChange={e => setFormData(prev => ({ ...prev, department_id: e.target.value }))}
              options={[
                { value: '', label: 'Select Department' },
                ...departments.map(d => ({ value: d.id, label: d.name })),
              ]}
            />
            <Select
              label="Role"
              required
              value={formData.role_id}
              onChange={e => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
              options={[
                { value: '', label: 'Select Role' },
                ...roles.map(r => ({ value: r.id, label: r.name })),
              ]}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="Employee ID"
              value={formData.employee_id}
              onChange={e => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
              placeholder="e.g. EMP-104"
            />
            <Input
              label="Job Title"
              value={formData.job_title}
              onChange={e => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
              placeholder="e.g. Senior Specialist"
            />
            <Input
              label="Phone Number"
              value={formData.phone}
              onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+1 555-0123"
            />
          </div>

          <div className="w-full sm:w-1/2">
            <Select
              label="Status"
              value={formData.status}
              onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'suspended', label: 'Suspended' },
              ]}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={formLoading}>
              {editingUser ? 'Save User' : 'Create User'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        title={`Reset Password: ${resetUser?.full_name}`}
        description="Provide a temporary password. The user will be required to choose a new password on their next sign-in."
        maxWidth="md"
      >
        <form onSubmit={handleResetPassword} className="space-y-4">
          <Input
            label="Temporary Password"
            required
            minLength={8}
            value={newTempPassword}
            onChange={e => setNewTempPassword(e.target.value)}
            helperText="At least 8 characters long"
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setResetModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" type="submit" loading={resetLoading}>
              Confirm Password Reset
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete User Confirmation */}
      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, user: null, loading: false })}
        onConfirm={handleDeleteUser}
        title="Confirm User Deletion"
        message={`Are you sure you want to delete and deactivate "${confirmDelete.user?.full_name}" (${confirmDelete.user?.email})? All active sessions for this user will be immediately revoked.`}
        confirmLabel="Deactivate & Delete"
        loading={confirmDelete.loading}
      />
    </div>
  );
};
